/**
 * Persian Text Component
 * Automatically handles Persian text rendering with proper RTL support,
 * bidirectional text handling, and CSS-in-JS styling
 */

import React, { useEffect, useRef, useMemo } from 'react';
import { persianLanguageService } from '../../services/persianLanguageService';
import type { PersianStyleOptions } from '../../services/persianLanguageService';
import type { PersianMetadata } from '../../types';

interface PersianTextProps {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  tag?: keyof JSX.IntrinsicElements;
  options?: PersianStyleOptions;
  convertDigits?: boolean;
  normalize?: boolean;
  metadata?: PersianMetadata;
  inline?: boolean;
  autoDirection?: boolean;
  enableBidi?: boolean;
  onClick?: () => void;
  onTextAnalysis?: (analysis: any) => void;
}

export const PersianText: React.FC<PersianTextProps> = ({
  children,
  className = '',
  style = {},
  tag: Tag = 'span',
  options = {},
  convertDigits = false,
  normalize = false,
  metadata,
  inline = true,
  autoDirection = true,
  enableBidi = true,
  onClick,
  onTextAnalysis,
}) => {
  const elementRef = useRef<HTMLElement>(null);

  // Memoize text analysis to avoid unnecessary recalculations
  const textAnalysis = useMemo(() => {
    // Extract text content from children
    const textContent = React.Children.toArray(children)
      .map(child => {
        if (typeof child === 'string') return child;
        if (typeof child === 'number') return String(child);
        if (React.isValidElement(child) && typeof child.props.children === 'string') {
          return child.props.children;
        }
        return '';
      })
      .join(' ');
    
    return persianLanguageService.analyzeText(textContent);
  }, [children]);

  // Memoize formatted text and styles
  const formattedData = useMemo(() => {
    // For complex children, we'll just use the original children and apply styles
    if (React.Children.count(children) > 1 || React.Children.toArray(children).some(child => React.isValidElement(child))) {
      return {
        text: children,
        direction: autoDirection ? textAnalysis.direction : (options.direction || 'auto'),
        className: ''
      };
    }
    
    // For simple string children, format the text
    const textContent = typeof children === 'string' ? children : String(children || '');
    const formatted = persianLanguageService.formatPersianText(textContent, {
      convertDigits,
      normalize,
      direction: autoDirection ? textAnalysis.direction : options.direction,
      applyFormatting: true,
    });

    return formatted;
  }, [children, convertDigits, normalize, autoDirection, textAnalysis.direction, options.direction]);

  // Generate CSS-in-JS styles based on text analysis and options
  const computedStyles = useMemo((): React.CSSProperties => {
    const baseStyles: React.CSSProperties = {
      // Font family with Persian font stack
      fontFamily: 'var(--font-persian, "Noto Serif Persian", "Tahoma", "Arial", sans-serif)',
      
      // Direction handling
      direction: formattedData.direction !== 'auto' ? formattedData.direction : undefined,
      
      // Unicode bidirectional text handling
      unicodeBidi: enableBidi ? (textAnalysis.containsPersian ? 'plaintext' : 'normal') : undefined,
      
      // Text alignment based on direction
      textAlign: (() => {
        if (options.textAlign && options.textAlign !== 'auto') return options.textAlign;
        if (textAnalysis.isPrimaryPersian) return 'right';
        if (formattedData.direction === 'rtl') return 'right';
        return undefined;
      })(),
      
      // Line height for better Persian text readability
      lineHeight: options.lineHeight || (textAnalysis.containsPersian ? '1.8' : '1.6'),
      
      // Font size optimization for Persian text
      fontSize: options.fontSize || (textAnalysis.containsPersian ? '1.1em' : undefined),
      
      // Font weight
      fontWeight: options.fontWeight,
      
      // Display mode
      display: inline ? 'inline' : 'block',
      
      // Word spacing for better Persian readability
      wordSpacing: textAnalysis.containsPersian ? '0.1em' : undefined,
      
      // Letter spacing
      letterSpacing: textAnalysis.containsPersian ? '0.02em' : undefined,
    };

    // Apply metadata styles if provided
    if (metadata?.cssProperties) {
      Object.assign(baseStyles, metadata.cssProperties);
    }

    // Merge with user-provided styles (user styles take precedence)
    return { ...baseStyles, ...style };
  }, [
    formattedData.direction,
    textAnalysis,
    options,
    enableBidi,
    inline,
    metadata,
    style
  ]);

  // Generate CSS classes based on text analysis
  const computedClassName = useMemo(() => {
    const classes = [
      'persian-text-component',
      formattedData.className,
      className
    ];

    // Add analysis-based classes
    if (textAnalysis.containsPersian) {
      classes.push('has-persian-text');
    }
    
    if (textAnalysis.isPrimaryPersian) {
      classes.push('primary-persian');
    }
    
    if (textAnalysis.direction === 'rtl') {
      classes.push('rtl-text');
    } else if (textAnalysis.direction === 'ltr') {
      classes.push('ltr-text');
    }

    // Add bidirectional class if text contains mixed scripts
    if (textAnalysis.persianCharacterCount > 0 && textAnalysis.englishCharacterCount > 0) {
      classes.push('bidi-text');
    }

    return classes.filter(Boolean).join(' ');
  }, [formattedData.className, className, textAnalysis]);

  // Apply Persian styles and handle metadata
  useEffect(() => {
    if (elementRef.current) {
      // Apply Persian language service styles
      const textForStyling = typeof formattedData.text === 'string' ? formattedData.text : '';
      persianLanguageService.applyPersianStyles(
        elementRef.current,
        textForStyling,
        options
      );

      // Process metadata if provided
      if (metadata) {
        persianLanguageService.processPersianMetadata(metadata);
      }

      // Set data attributes for CSS targeting
      elementRef.current.setAttribute('data-text-direction', formattedData.direction);
      elementRef.current.setAttribute('data-has-persian', textAnalysis.containsPersian.toString());
      elementRef.current.setAttribute('data-primary-persian', textAnalysis.isPrimaryPersian.toString());
      elementRef.current.setAttribute('data-bidi', (textAnalysis.persianCharacterCount > 0 && textAnalysis.englishCharacterCount > 0).toString());
    }
  }, [formattedData, options, metadata, textAnalysis]);

  // Notify parent component of text analysis
  useEffect(() => {
    if (onTextAnalysis) {
      onTextAnalysis(textAnalysis);
    }
  }, [textAnalysis, onTextAnalysis]);

  return (
    <Tag
      ref={elementRef}
      className={computedClassName}
      style={computedStyles}
      onClick={onClick}
      dir={formattedData.direction !== 'auto' ? formattedData.direction : undefined}
      lang={textAnalysis.containsPersian ? 'fa' : undefined}
    >
      {formattedData.text}
    </Tag>
  );
};

export default PersianText;